import AsyncStorage from '@react-native-async-storage/async-storage';
import type { StoreApi, UseBoundStore } from 'zustand';
import { create } from 'zustand';
import type { PersistOptions } from 'zustand/middleware';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { StateCreator } from 'zustand/vanilla';
import { useExtraConfigContext } from '@gov-nx/core/app/extra-config/ExtraConfigProvider';

export enum StoreKeys {
	app = 'app',
	auth = 'auth',
	dataBoxSearch = 'dataBoxSearch',
	authorization = 'authorization',
	user = 'user',
	cms = 'cms',
	filters = 'filters',
	forms = 'forms',
	wizard = 'wizard',
	configuration = 'configuration',
	translations = 'translations',
	signpost = 'signpost',
	drozdRegistrations = 'drozdRegistrations',
	drozdSignpost = 'drozdSignpost',
	drozdDocuments = 'drozdDocuments',
}

/**
 * Build the persisted-storage key for a store. The `persisKey` namespace comes
 * from `extraConfig.store.persisKey` so the key can be derived without a live
 * store instance (e.g. to wipe sensitive data on de-registration).
 */
export const persistedStoreKey = (
	persisKey: string,
	name: StoreKeys | keyof typeof StoreKeys
) => `${persisKey}:${name}`;

const stores = new Map();

export const getStore = <State>(
	name: keyof typeof StoreKeys
): UseBoundStore<StoreApi<State>> => stores.get(name);

export const usePersistedStore = <State, T>(
	creator: StateCreator<State>,
	name: keyof typeof StoreKeys,
	selector: (state: State) => T,
	persisConfig?: Omit<PersistOptions<State>, 'name'>
) => {
	const { extraConfig } = useExtraConfigContext();

	if (!stores.has(name)) {
		const created = create<State>();

		const store = created(
			persist<State>(creator, {
				name: persistedStoreKey(extraConfig.store.persisKey, name),
				storage: createJSONStorage(() => AsyncStorage),
				version: 1,
				...persisConfig,
			})
		);

		stores.set(name, store);
	}

	const store = getStore<State>(name);

	return store(selector);
};
